import React, {useEffect, useLayoutEffect, useMemo, useState} from 'react';
import MainLayout from '../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {
  LayoutChangeEvent,
  Platform,
  RefreshControl,
  View,
  ScrollView,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {Theme} from '../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Icon from '../../../components/icon';
import screens from '../../../config/screens';
import Animated, {
  Extrapolation,
  FadeInDown,
  FadeInUp,
  FadeOutUp,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import TextGeneric from '../../../components/textGeneric';
import EmptyList from '../../../components/emptyList';
import {tr} from '../../../translations';
import Loader from '../../../components/loader';
import {SummariesScreenProps} from '../../../interfaces/screens/summaries';
import {NewsSummeryItem} from '../../../interfaces/api/summaries';
import GenericButton from '../../../components/button';
import {openDateRangePickerModal} from '../../../connected-components/dateRangePickerModal/actions';
import HeaderSearch from '../../../components/header/headerSearch';
import {showFiltersModal} from '../../../connected-components/filtersModal/actions';
import {NewsOptionTab} from '../../../utils/enums';
import {
  getAnalysisByDate,
  getPredictionsByDate,
  getSummariesByDate,
} from './actions';
import dayjs, {Dayjs} from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import {Space} from '../home';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import {getPublishersByLetter} from '../tabNews/actions';
import {DrugItemType} from '../../../interfaces/api/tabs';
import {handleChange} from '../../../utils/form';
import {
  FilterItemContainer,
  FilterLabel,
  FiltersContainer,
  FilterValuesContainer,
} from '../drugs';
import '../../../utils/webAnimations';
import {FilterType} from '../../../interfaces/reducers/homeState';
import DrugFilterItem from '../../../components/drugFilterItem';
import {LinearGradient} from 'react-native-linear-gradient';
import GenioLatestNews from '../../../components/genioLatestNews';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {hdp, isWeb} from '../../../utils/responsive';
import SearchListSkeleton from '../../../components/searchListSkeleton';

dayjs.extend(minMax);

const Row = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({theme}) => theme.text.s8}px ${({theme}) => theme.text.s55}px;
`;
const DateFilterContainer = styled(Animated.View)`
  width: 100%;
  border-bottom-right-radius: 30px;
  border-bottom-left-radius: 30px;
  overflow: hidden;
  position: absolute;
  top: 0;
  z-index: 22;
`;
const DateFilterContentContainer = styled.View`
  width: 100%;
  padding-top: ${({theme}) => (isWeb ? theme.text.s55 + 'px' : '10%')};
`;
const DateView = styled(GenericButton)`
  background-color: transparent;
  border-width: 1px;
  border-radius: 12px;
  border-color: ${({theme}) => theme.text.light};
  margin-horizontal: ${({theme}) => theme.text.s10}px;
`;
const DateTitle = styled(TextGeneric)`
  width: 100%;
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.light};
  padding-horizontal: ${({theme}) => theme.text.s55}px;
  padding-bottom: ${({theme}) => theme.text.s6}px;
  text-align: left;
`;
const BottomIconContainer = styled.View`
  width: ${({theme}) => theme.text.s2}px;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
`;
const FiltersItemsContainer = styled(FiltersContainer)<{
  responsiveWidth: number;
}>`
  width: ${({responsiveWidth}) => responsiveWidth}px;
  border-radius: 20px;
  margin-vertical: ${({theme}) => theme.text.s55}px;
`;
const IconContainer = styled(Animated.View)<{isPositionLeft?: boolean}>`
  position: absolute;
  top: ${({theme}) => theme.text.s12}px;
  ${({isPositionLeft, theme}) =>
    isPositionLeft
      ? `left:-${theme.text.s12}px;`
      : `right:-${theme.text.s12}px;`};
  z-index: 99;
`;
const SummaryItem = styled(GenioLatestNews)<{responsiveWidth: string}>`
  width: ${({responsiveWidth}) => responsiveWidth};
  height: ${({theme}) => theme.text.s1 * 3}px;
  align-self: center;
  margin-bottom: ${({theme}) => theme.text.s12}px;
`;
const EmptyContainer = styled.View<{
  extraSpace?: number;
  responsiveWidth?: string;
}>`
  align-items: center;
  justify-content: center;
  align-self: center;
  width: ${({responsiveWidth}) => responsiveWidth};
  min-height: ${({extraSpace}) => hdp(75 - (extraSpace || 0) / 4)}px;
`;

const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

/**
 * Summaries screen component.
 *
 * @param {SummariesScreenProps} props - The screen component props.
 */
const Summaries: React.FC<SummariesScreenProps> = ({navigation, route}) => {
  const theme = useTheme() as Theme;
  const headerHeight = useCustomHeaderHeight();
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const {
    tabId,
    tabFilters,
    filters: prevFiltersResult,
    summaryDateInfo,
    type = NewsOptionTab.Summaries,
  } = route?.params || {};
  const publisherIdx = (tabFilters || []).findIndex(
    (i: any) => i.key === 'publisher',
  );

  const [filtersResult, setFiltersResult] = useState<any>(
    prevFiltersResult || {},
  );
  const [data, setData] = useState<NewsSummeryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [filterHeight, setFilterHeight] = useState(0);
  const handleLayout = (event: any) => {
    const {height} = event.nativeEvent.layout;
    setFilterHeight(height);
  };
  // Publishers filter
  const [isLoadingPublishers, setIsLoadingPublishers] = useState(false);
  const [isErrorPublishers, setIsErrorPublishers] = useState(false);
  const [publishersPage, setPublishersPage] = useState(1);
  const [isLastPagePublishers, setIsLastPagePublishers] =
    useState<boolean>(false);
  const [publishersData, setPublishersData] = useState<DrugItemType[]>([]);
  const [isPublisherVisible, setIsPublisherVisible] = useState(false);
  const [isFilterModalShouldClose, setIsFilterModalShouldClose] =
    useState<any>(false);
  const [selectedPublisher, setSelectedPublisher] = useState<any>({});
  const [selectedPublisherFilter, setSelectedPublisherFilter] = useState<any>(
    {},
  );
  const [selectedPublisherAlphabet, setSelectedPublisherAlphabet] =
    useState('');
  const [publisherHeight, setPublisherHeight] = useState(hdp(5));
  const scrollY = useSharedValue(0);
  const filtersNum = useMemo(
    () => Object.values(filtersResult || {}).filter(Boolean).length,
    [filtersResult],
  );
  const [summaryDate, setSummaryDate] = useState<Dayjs>(
    (summaryDateInfo?.lastSummaryDates && summaryDateInfo.lastSummaryDates.length > 0)
      ? summaryDateInfo.lastSummaryDates[summaryDateInfo.lastSummaryDates.length - 1]
      : summaryDateInfo?.latestSummaryDate,
  );

  // Add refs and state for scroll handling
  const [_contentHeight, setContentHeight] = useState(0);
  const [_containerHeight, setContainerHeight] = useState(0);
  const [_currentScrollPosition, setCurrentScrollPosition] = useState(0);
  const loadMoreThreshold = 0.75; // Trigger when 75% scrolled

  // Scroll handler for animations - used on mobile
  const animatedScrollHandler = useAnimatedScrollHandler(event => {
    scrollY.value = event.contentOffset.y;
  });

  // Web scroll handler that manages both animation and pagination
  const webScrollHandler = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    // Update animation value
    scrollY.value = event.nativeEvent.contentOffset.y;

    // Calculate if we need to load more content
    const {contentOffset, contentSize, layoutMeasurement} = event.nativeEvent;

    // Update tracking variables
    setCurrentScrollPosition(contentOffset.y);
    setContentHeight(contentSize.height);
    setContainerHeight(layoutMeasurement.height);

    // Calculate scroll percentage - how far down the user has scrolled
    const scrollPercentage =
      (contentOffset.y + layoutMeasurement.height) / contentSize.height;

    // If we've scrolled past the threshold, load more content
    if (scrollPercentage >= loadMoreThreshold && !isLoading && !isLastPage) {
      loadMore();
    }
  };

  const loadMore = async (refresh = false) => {
    if (isLoading) {
      return;
    }
    setIsError(false);
    if (!isLastPage || refresh) {
      if (refresh) {
        setData([]);
      }
      setIsLoading(true);
      const body = {
        tabId,
        page: refresh ? 1 : page,
        filters: filtersResult,
        dateFrom: dayjs(summaryDate).format('YYYY-MM-DD'),
      };
      let result: any = null;
      if (type === NewsOptionTab.Analysis) {
        result = await getAnalysisByDate(body);
      } else if (type === NewsOptionTab.Predictions) {
        result = await getPredictionsByDate(body);
      } else {
        result = await getSummariesByDate(body);
      }
      if (!result) {
        setIsError(true);
        setIsLoading(false);
        setIsLastPage(true);
        return;
      }
      setData(prev => {
        if (refresh) {
          return result?.data || [];
        } else {
          return [...prev, ...(result?.data || [])].filter(
            (value, index, self) => {
              return (
                self.findIndex(obj =>
                  ['id'].every(key => obj[key] === value[key]),
                ) === index
              );
            },
          );
        }
      });
      setPage(prev => (refresh ? 2 : prev + 1));
      const totalPageCount = Math.ceil(
        parseFloat(result?.total || '2') / parseFloat(result?.pageSize || 10),
      );
      setIsLastPage((refresh ? 1 : page) >= totalPageCount);
      setIsLoading(false);
    }
  };

  const loadMorePublishers = async (refresh = false) => {
    if (isLoadingPublishers || !isPublisherVisible) {
      return;
    }
    setIsErrorPublishers(false);
    if (!isLastPagePublishers || refresh) {
      if (refresh) {
        setPublishersData([]);
      }
      setIsLoadingPublishers(true);
      const result = await getPublishersByLetter({
        tabId,
        page: refresh ? 1 : publishersPage,
        pageSize: 20,
        ...filtersResult,
        // keywords: selectedPublisherFilter?.publisher || '',
        ...selectedPublisherFilter,
      });

      if (!result) {
        setIsErrorPublishers(true);
        setIsLoadingPublishers(false);
        setIsLastPagePublishers(true);
        return;
      }

      setPublishersData(prev => {
        if (refresh) {
          return result?.data || [];
        } else {
          return [...prev, ...(result?.data || [])].filter(
            (value: any, index, self) => {
              return (
                self.findIndex((obj: any) =>
                  ['id'].every(key => obj[key] === value[key]),
                ) === index
              );
            },
          );
        }
      });

      setPublishersPage(prev => (refresh ? 2 : prev + 1));
      const totalPageCount = Math.ceil(
        parseFloat(result?.total ? result?.total + '' : '2') / 10,
      );
      setIsLastPagePublishers((refresh ? 1 : publishersPage) >= totalPageCount);
      setIsLoadingPublishers(false);
    }
  };

  useEffect(() => {
    if (summaryDate) {
      loadMore(true);
    }
  }, [summaryDate, filtersResult]);

  useEffect(() => {
    if (publisherIdx > -1) {
      loadMorePublishers(true);
    }
  }, [selectedPublisherFilter, tabId]);

  const _renderFooter = () => (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: isLoading && data?.length > 0 ? '5%' : 0,
      }}>
      {isLoading && data?.length > 0 && <Loader color={theme.primary} />}
      <Space bottomInset={bottomInset} extraSpace={hdp(15)} />
    </View>
  );

  const _renderEmpty = () => {
    const extraSpace =
      hdp(10) + (isPublisherVisible ? publisherHeight + hdp(2) : 0);
    if (isError) {
      return (
        <EmptyContainer
          responsiveWidth={getByScreenSize('100%', '40%')}
          extraSpace={extraSpace}>
          <EmptyList
            iconName={'alert'}
            iconColor={theme.primary}
            iconSize={theme.text.s1 * 2}
            onRefreshPress={() => loadMore(true)}
            title={'app.error'}
            showRefresh
          />
        </EmptyContainer>
      );
    } else if (isLoading) {
      return (
        <SearchListSkeleton marginBottom={theme.text.s12} numberOfItems={4} />
      );
    } else {
      return (
        <EmptyContainer
          responsiveWidth={getByScreenSize('100%', '40%')}
          extraSpace={extraSpace}>
          <EmptyList
            iconName={'empty'}
            iconColor={theme.primary}
            iconSize={theme.text.s1 * 2}
            title={'app.empty'}
          />
        </EmptyContainer>
      );
    }
  };

  const openFilterModal = () => {
    const tempFilters = tabFilters.filter((i: any) => i.key !== 'publisher');
    if (tempFilters === null) {
      return;
    }
    showFiltersModal({
      filters: tempFilters,
      filtersValues: filtersResult,
      onSubmit: val => setFiltersResult(val),
    });
  };

  const _renderFilterItem = (item: FilterType) => {
    return (
      <FilterItemContainer key={item?.key}>
        <IconContainer>
          <Icon
            name={'x'}
            color={theme.text.grey}
            size={theme.text.s55}
            role={'button'}
            onPress={() => setIsPublisherVisible(false)}
          />
        </IconContainer>
        <FilterLabel semiBold>
          {item?.label || 'Find Publisher by Alphabet'}
        </FilterLabel>
        <FilterValuesContainer>
          {(item?.items || []).map(filterItem => (
            <DrugFilterItem
              key={filterItem?.id}
              name={filterItem?.value}
              data={publishersData || []}
              onPress={() => {
                setIsFilterModalShouldClose(false);
                if (item.key) {
                  setSelectedPublisherFilter({[item.key]: filterItem.value});
                }
              }}
              onConfirmSelection={(selectedItem: any) => {
                if (!selectedItem) {
                  return;
                }
                setSelectedPublisher(selectedItem);
                if (Object.values(selectedItem).length > 0) {
                  handleChange(
                    item.key || '',
                    //@ts-ignore
                    Object.values(selectedItem || {})?.[0]?.id,
                    setFiltersResult,
                  );
                }
                if (Object.keys(selectedItem || {}).length > 0) {
                  setSelectedPublisherAlphabet(
                    Object.keys(selectedItem || {})[0],
                  );
                }
              }}
              selectedDrug={selectedPublisher}
              isLoading={isLoadingPublishers}
              isError={isErrorPublishers}
              onRefreshPress={() => loadMorePublishers(true)}
              onEndReached={() => loadMorePublishers()}
              isFilterModalShouldClose={isFilterModalShouldClose}
            />
          ))}
        </FilterValuesContainer>
      </FilterItemContainer>
    );
  };

  const renderHeader = () => {
    return (
      <HeaderSearch
        filters={tabFilters || []}
        filtersResult={filtersResult || {}}
        onFilterPress={openFilterModal}
        setFiltersResult={setFiltersResult}
        scrollY={scrollY}
        newsCount={data.length + '' || '0'}
        isLoading={isLoading && data.length === 0}
        isSearchHidden
        isFiltersHidden={
          (tabFilters || []).filter((i: any) => i.key !== 'publisher')
            .length === 0
        }
        publisherActiveFilter={selectedPublisherAlphabet}
        publisherActiveName={
          selectedPublisher?.[selectedPublisherAlphabet]?.name || ''
        }
        onPublisherClear={() => {
          handleChange('publisher', '', setFiltersResult);
          setIsPublisherVisible(false);
          setSelectedPublisherAlphabet('');
          setSelectedPublisher({});
        }}
        showPublisherIcon={publisherIdx > -1}
        onPublisherPress={() => setIsPublisherVisible(prev => !prev)}
        renderAdditionalFilters={() => {
          if (!isPublisherVisible) {
            return <React.Fragment />;
          }
          return (
            <FiltersItemsContainer
              responsiveWidth={wdp(getByScreenSize(94, 70))}
              onLayout={(event: LayoutChangeEvent) => {
                const {height} = event.nativeEvent.layout;
                setPublisherHeight(height);
              }}
              {...(Platform.OS === 'web'
                ? {className: 'screen-fade-in-up'}
                : {entering: FadeInUp, exiting: FadeOutUp})}>
              {publisherIdx > -1 &&
                _renderFilterItem(tabFilters?.[publisherIdx])}
            </FiltersItemsContainer>
          );
        }}
      />
    );
  };

  useLayoutEffect(() => {
    navigation?.setOptions({
      header: renderHeader,
      headerShown: true,
    });
  }, [
    tabFilters,
    filtersResult,
    isLoading,
    data,
    filtersResult,
    isLoading,
    data,
    publishersData,
    publishersPage,
    selectedPublisher,
    isLoadingPublishers,
    isErrorPublishers,
    isPublisherVisible,
    selectedPublisherFilter,
    selectedPublisherAlphabet,
    isLastPagePublishers,
    isFilterModalShouldClose,
  ]);

  const _renderItems = ({
    item,
    index,
  }: {
    item: NewsSummeryItem;
    index: number;
  }) => {
    return (
      <Animated.View entering={FadeInDown}>
        <SummaryItem
          responsiveWidth={Platform.OS === 'web' ? '94%' : wdp(94) + 'px'}
          key={item.id}
          index={index}
          title={
            (item?.subspecialty || '') +
            ' / ' +
            (item?.typeofstudiesandreaserch || '') +
            (item?.resource
              ? `\n\n${tr('summaries.resource')}: ` + item?.resource
              : '\n\n')
          }
          specialty={item?.subspecialty || item?.specialty || ''}
          image={{uri: item?.img || ''}}
          onPress={() =>
            navigation?.navigate(screens.content.latestArticle, {
              item,
              isSummary: type,
            })
          }
        />
      </Animated.View>
    );
  };

  const onDatePress = (type: 'prev' | 'next') => {
    if (
      !(
        Array.isArray(summaryDateInfo?.lastSummaryDates) &&
        summaryDateInfo?.lastSummaryDates.length > 0
      )
    ) {
      return;
    }
    // Find the current index in the sorted dates
    const currentIndex = summaryDateInfo?.lastSummaryDates.findIndex(date =>
      date.isSame(summaryDate, 'day'),
    );

    if (currentIndex === -1) {
      return;
    }

    if (type === 'prev' && currentIndex > 0) {
      // Move to the previous date
      setSummaryDate(summaryDateInfo?.lastSummaryDates[currentIndex - 1]);
    } else if (
      type === 'next' &&
      currentIndex < summaryDateInfo?.lastSummaryDates.length - 1
    ) {
      // Move to the next date
      setSummaryDate(summaryDateInfo?.lastSummaryDates[currentIndex + 1]);
    } else {
    }
  };

  const filtersStyle = useAnimatedStyle(() => {
    const translateY = interpolate(
      scrollY.value,
      [
        0,
        filtersNum > 0
          ? (headerHeight || 0) * 0.6 + filterHeight
          : filterHeight,
      ],
      [filtersNum > 0 ? (headerHeight || 0) * 0.6 : 0, -filterHeight],
      Extrapolation.CLAMP,
    );

    return {
      transform: [{translateY: translateY}],
      paddingTop: topInset,
    };
  });

  const _renderFilterHeader = () => {
    const currentIndex =
      summaryDateInfo?.lastSummaryDates?.findIndex(date =>
        date.isSame(summaryDate, 'day'),
      ) || 0;
    return (
      <DateFilterContainer style={filtersStyle}>
        <LinearGradient
          colors={[
            theme.drugsDiseases.gradientTopLeft,
            theme.drugsDiseases.gradientMidTop,
            theme.drugsDiseases.gradientCenter,
            theme.drugsDiseases.gradientBottomRight,
          ]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 1}}
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <DateFilterContentContainer onLayout={handleLayout}>
            <DateTitle semiBold>{tr(`summaries.${type}DateFilter`)}</DateTitle>
            <DateView
              disabled={isLoading && data.length === 0}
              onPress={() => {
                openDateRangePickerModal({
                  onConfirm: val => {
                    if (val?.date) {
                      setSummaryDate(dayjs(val?.date));
                    }
                  },
                  datePickerProps: {
                    mode: 'single',
                    // disabledDates: (date: any) =>
                    //   !().some(
                    //     enabledDate => enabledDate.isSame(date, 'day'),
                    //   ),
                    enabledDates: summaryDateInfo?.lastSummaryDates || [],
                    minDate: dayjs.min(summaryDateInfo?.lastSummaryDates || []),
                    maxDate: dayjs.max(summaryDateInfo?.lastSummaryDates || []),
                  },
                  currentRange: {
                    date: summaryDate,
                  },
                });
              }}
              outlineBackgroundColor={theme.primary}
              backgroundColor={'transparent'}
              label={dayjs(summaryDate).format('LL')}
              style={{
                width: getByScreenSize('90%', '50%'),
                alignSelf: 'center',
              }}
              showRightIcon
              rightIconName={'calendar'}
              rightIconSize={theme.text.s5}
              rightIconColor={theme.text.light}
            />
            <Row>
              <GenericButton
                disabled={(isLoading && data.length === 0) || currentIndex <= 1}
                onPress={() => onDatePress('prev')}
                style={{width: wdp(40), backgroundColor: 'transparent'}}
                isSmall
                label={'Previous Day'}
                backgroundColor={theme.cardBackground}
                customLeftComponent={() => (
                  <BottomIconContainer style={{marginRight: theme.text.s9 / 2}}>
                    <Icon
                      name={'arrowRightSmall'}
                      color={theme.primary}
                      size={theme.text.s6}
                      style={{
                        transform: [{rotate: '180deg'}],
                      }}
                    />
                  </BottomIconContainer>
                )}
              />
              <GenericButton
                onPress={() => onDatePress('next')}
                isSmall
                disabled={
                  currentIndex >=
                    (summaryDateInfo?.lastSummaryDates || []).length - 1 ||
                  (isLoading && data.length === 0)
                }
                label={'Next Day'}
                backgroundColor={theme.cardBackground}
                style={{width: wdp(40), backgroundColor: 'transparent'}}
                customRightComponent={() => (
                  <BottomIconContainer style={{marginLeft: theme.text.s9 / 2}}>
                    <Icon
                      name={'arrowRightSmall'}
                      color={theme.primary}
                      size={theme.text.s6}
                    />
                  </BottomIconContainer>
                )}
              />
            </Row>
          </DateFilterContentContainer>
        </LinearGradient>
      </DateFilterContainer>
    );
  };

  const renderScrollView = () => {
    const contentContainerStyle = {
      flexGrow: 1,
      paddingHorizontal: wdp(getByScreenSize(3, 15)),
      paddingTop:
        topInset / 2 +
        filterHeight +
        (filtersNum > 0 ? theme.text.s2 + headerHeight * 0.6 : theme.text.s2) +
        (isPublisherVisible ? publisherHeight + theme.text.s2 : 0),
      minHeight: hdp(100),
    };
    return (
      <AnimatedScrollView
        style={{
          height: isWeb ? hdp(100) : 'auto',
        }}
        contentContainerStyle={contentContainerStyle}
        onScroll={isWeb ? webScrollHandler : animatedScrollHandler}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl
            tintColor={theme.primary}
            refreshing={isLoading && data?.length === 0}
            onRefresh={() => loadMore(true)}
            progressViewOffset={
              topInset / 2 +
              filterHeight +
              (filtersNum > 0
                ? theme.text.s2 + headerHeight * 0.6
                : theme.text.s2) +
              (isPublisherVisible ? publisherHeight + theme.text.s2 : 0)
            }
          />
        }>
        {data.length > 0
          ? data.map((item, index) => (
              <View key={(item?.id || '1').toString() + index}>
                {_renderItems({item, index})}
              </View>
            ))
          : _renderEmpty()}
        {_renderFooter()}
      </AnimatedScrollView>
    );
  };

  return (
    <MainLayout
      showOverlay={false}
      enableScroll={false}
      isCustomHeader
      noPadding
      noPaddingHeader>
      {_renderFilterHeader()}
      {renderScrollView()}
    </MainLayout>
  );
};

export default Summaries;
